/**
 * Student side of live tests: the list, and the test itself.
 *
 * While a test is open the page watches for the student leaving it (another tab, another window, leaving full
 * screen) and tells the server, which counts the switches and submits the test when the limit is reached. The
 * server owns the clock and the count; this page only reports what the browser saw and shows what the server says.
 */
import { api } from '../../core/api.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { fmtDateTime } from '../../core/format.js';
import { currentView, every, update } from '../../app/shell.js';
import { backLink, banner, empty, ic, tag } from '../../ui/components.js';
import { confirmBox } from '../../ui/modal.js';
import { toast } from '../../ui/toast.js';
import { REASON, clock } from './lib.js';

const REPORT_GAP_MS = 3000;     // one tab switch fires several browser events; send one report
const SAVE_EVERY_MS = 15000;

const run = { deadline: 0, detach: null, lastReport: 0, saved: '', submitting: false, fullscreen: false };

const canFullscreen = () => Boolean(document.documentElement.requestFullscreen);
const goFullscreen = async () => {
  if (!canFullscreen() || document.fullscreenElement) return;
  try { await document.documentElement.requestFullscreen(); } catch { /* the browser may refuse; the page still watches focus */ }
};
const leaveFullscreen = async () => {
  if (document.fullscreenElement) { try { await document.exitFullscreen(); } catch { /* ignore */ } }
};

/* ---------- list ---------- */

const STATE = {
  upcoming: (t) => html`${tag('Not open yet', 'wn')} <span class="mu sm">Opens ${fmtDateTime(t.opens_at)}</span>`,
  open: () => tag('Open now', 'ok'),
  in_progress: () => tag('In progress', 'wn'),
  closed: () => tag('Closed', 'n'),
  done: (t) => html`${t.result.passed ? tag('Passed', 'ok') : tag('Below pass mark', 'bad')} <b>${t.result.percent}%</b>`,
};

const list = {
  path: '/student/tests',
  roles: ['student'],
  title: 'Tests',
  load: (ctx) => api.get('/student/tests', { signal: ctx.signal }),
  view(d) {
    return html`<div class="page-h"><div><h2 style="margin:0 0 4px">Tests</h2><p>Timed tests set by your trainer. Use a laptop or desktop and stay on the page.</p></div></div>
      ${d.tests.length
        ? html`<div class="list">${d.tests.map((t) => html`<div class="card mb"><div class="row between"><div><h3 style="margin:0 0 4px">${t.title}</h3>
            <p class="mu sm" style="margin:0">${t.question_count} questions, ${t.duration_min} minutes. Open ${fmtDateTime(t.opens_at)} to ${fmtDateTime(t.closes_at)}.</p></div>
            <div class="row">${STATE[t.state](t)}
            ${t.state === 'open' || t.state === 'in_progress' ? html`<a class="btn sm" href="/student/tests/${t.id}">${t.state === 'open' ? 'Start' : 'Continue'}</a>` : t.state === 'done' ? html`<a class="btn ghost sm" href="/student/tests/${t.id}">Result</a>` : ''}</div></div></div>`)}</div>`
        : empty('clip', 'No tests yet', 'When your trainer sets a test it shows up here and you get a notification.')}`;
  },
};

/* ---------- the test ---------- */

const testPage = {
  path: '/student/tests/:id',
  roles: ['student'],
  nav: 'tests',
  title: (d) => (d ? d.test.title : 'Test'),
  ui: () => ({ phase: 'intro', paper: null, answers: [], violations: 0, max: 3, warn: false, result: null, left: 0, busy: false }),
  async load(ctx) {
    const { tests } = await api.get('/student/tests', { signal: ctx.signal });
    const test = tests.find((t) => t.id === Number(ctx.params.id));
    if (!test) throw Object.assign(new Error('This test is not available to you.'), { status: 404 });
    return { test };
  },
  enter() {
    every(1000, () => tick());
    every(SAVE_EVERY_MS, () => saveNow());
  },
  leave() {
    stopWatching();
    void leaveFullscreen();
  },
  view(d, ui) {
    if (ui.phase === 'taking') return taking(d.test, ui);
    const result = ui.result || (d.test.state === 'done' ? d.test.result : null);
    if (result) return finished(d.test, result);
    return intro(d.test, ui);
  },
};

function intro(t, ui) {
  const resume = t.state === 'in_progress';
  const canStart = t.state === 'open' || resume;
  return html`${backLink('/student/tests', 'Tests')}
    <div class="page-h"><div><h2 style="margin:0 0 4px">${t.title}</h2><p>${t.question_count} questions, ${t.duration_min} minutes.</p></div></div>
    ${t.instructions ? html`<div class="card mb"><b>From your trainer</b><p style="white-space:pre-line;margin:6px 0 0">${t.instructions}</p></div>` : ''}
    <div class="card mb"><h3 style="margin-top:0">Before you start</h3>
      <ul class="sm" style="line-height:1.7;margin:0;padding-left:20px">
        <li>You get <b>${t.duration_min} minutes</b> from the moment you press Start. The timer runs on our server, so refreshing does not give extra time.</li>
        <li>The test runs <b>full screen</b>. Do not switch tabs, open another window or leave full screen.</li>
        <li>Every switch is recorded and your trainer can see it. After <b>${t.max_violations} switches</b> the test is submitted automatically with the answers you have given.</li>
        <li>Refreshing the page or coming back later also counts as a switch.</li>
        <li>Copy, paste and right-click are switched off. You have one attempt.</li>
        <li>Your answers are saved as you go. Press <b>Submit</b> when you are done.</li>
      </ul></div>
    ${resume ? banner('You already started this test. Continuing counts as a switch and the clock has kept running.', 'wn', 'alert') : ''}
    ${ui.error ? banner(ui.error, 'bad', 'alert') : ''}
    ${canStart ? html`<button type="button" class="btn lg" data-act="lt.start" ${ui.busy ? 'disabled' : ''}>${ic('play')} ${resume ? 'Continue the test' : 'I understand, start the test'}</button>`
      : t.state === 'upcoming' ? banner(`This test opens ${fmtDateTime(t.opens_at)}.`, '', 'info') : banner('This test is closed. Ask your trainer if you missed it.', 'wn', 'alert')}`;
}

function taking(t, ui) {
  const answered = ui.answers.filter((a) => a != null).length;
  const n = ui.paper.length;
  return html`<div class="card mb" style="position:sticky;top:0;z-index:5"><div class="row between">
      <div><b>${t.title}</b><div class="mu sm">Answered ${answered} of ${n}</div></div>
      <div class="row">${ui.violations ? tag(`Tab switches: ${ui.violations} of ${ui.max}`, ui.violations >= ui.max - 1 ? 'bad' : 'wn') : ''}
        <span id="lt-timer" class="tag ${ui.left <= 60 ? 'bad' : 'n'}" style="font-size:1.1rem" role="timer" aria-label="Time left">${clock(ui.left)}</span></div></div></div>
    ${ui.paper.map((q, i) => html`<div class="card mb"><p style="margin:0 0 10px"><b>${i + 1}. ${q.q}</b></p>
      ${q.options.map((o, j) => html`<label class="opt ${ui.answers[i] === j ? 'on' : ''}"><input type="radio" name="lq-${i}" data-act="lt.pick" data-i="${i}" data-j="${j}" ${ui.answers[i] === j ? 'checked' : ''}> <span>${o}</span></label>`)}</div>`)}
    <div class="row mb"><button type="button" class="btn lg" data-act="lt.submit" ${ui.busy ? 'disabled' : ''}>${ic('check')} Submit test</button>
      <span class="mu sm">${n - answered ? `${n - answered} unanswered` : 'All answered'}</span></div>
    ${ui.warn ? html`<div class="lt-warn" role="alertdialog" aria-modal="true" aria-label="Warning"><div class="card" style="max-width:480px">
        <h3 style="margin-top:0;color:var(--bad,#c0392b)">Warning ${ui.violations} of ${ui.max}</h3>
        <p>You left the test page. This has been recorded and your trainer can see it.</p>
        <p><b>${ui.max - ui.violations === 1 ? 'One more' : `${ui.max - ui.violations} more`} and the test is submitted automatically.</b></p>
        <button type="button" class="btn lg" data-act="lt.resume">Back to the test</button></div></div>` : ''}`;
}

function finished(t, r) {
  return html`${backLink('/student/tests', 'Tests')}
    <div class="card" style="max-width:520px;margin:20px auto;text-align:center">
      ${ic(r.passed ? 'check' : 'info')}<h2 style="margin:8px 0 4px">${t.title}</h2>
      <div class="stat" style="margin:12px 0"><div class="k" style="font-size:2.4rem">${r.percent}%</div><div class="l">${r.score} of ${r.total} correct</div></div>
      ${r.passed ? tag('Passed', 'ok') : tag(`Below the pass mark of ${t.pass_percent}%`, 'bad')}
      <p class="mu sm mt-s">${REASON[r.end_reason] || 'Submitted'}${r.violations ? `. Tab switches recorded: ${r.violations}.` : '.'}</p>
      ${r.end_reason === 'violations' ? banner('The test was submitted because you left the page too many times. Talk to your trainer if this was a mistake.', 'wn', 'alert') : ''}</div>`;
}

/* ---------- running a test ---------- */

function applyPaper(res) {
  const view = currentView();
  const ui = view.ui;
  ui.violations = res.violations ?? ui.violations;
  ui.max = res.max_violations ?? ui.max;
  if (res.finished) { finish(res.result); return; }
  ui.paper = res.questions || ui.paper;
  if (res.answers) ui.answers = res.answers;
  run.deadline = performance.now() + res.seconds_left * 1000;
  ui.left = res.seconds_left;
  run.saved = JSON.stringify(ui.answers);
  ui.phase = 'taking';
  startWatching();
}

function finish(result) {
  stopWatching();
  void leaveFullscreen();
  const ui = currentView().ui;
  ui.phase = 'result';
  ui.result = result;
  ui.warn = false;
  update();
}

function tick() {
  const view = currentView();
  if (view?.ui?.phase !== 'taking') return undefined;
  const left = Math.max(0, Math.ceil((run.deadline - performance.now()) / 1000));
  view.ui.left = left;
  const el = document.getElementById('lt-timer');
  if (el) { el.textContent = clock(left); el.className = `tag ${left <= 60 ? 'bad' : 'n'}`; }
  return left === 0 ? send(true) : undefined;
}

async function saveNow() {
  const view = currentView();
  if (view?.ui?.phase !== 'taking' || run.submitting) return;
  const body = JSON.stringify(view.ui.answers);
  if (body === run.saved) return;
  run.saved = body;
  const res = await api.put(`/student/tests/${view.data.test.id}/answers`, { body: { answers: view.ui.answers } });
  if (res.finished) await reloadResult();
}

async function reloadResult() {
  const view = currentView();
  const res = await api.post(`/student/tests/${view.data.test.id}/start`);
  applyPaper(res);
}

async function send(auto) {
  const view = currentView();
  if (run.submitting || view?.ui?.phase !== 'taking') return;
  run.submitting = true;
  try {
    const res = await api.post(`/student/tests/${view.data.test.id}/submit`, { body: { answers: view.ui.answers } });
    if (auto) toast('Time is up. Your test was submitted.', 'clip');
    finish(res.result);
  } finally {
    run.submitting = false;
  }
}

async function report(kind) {
  const view = currentView();
  if (view?.ui?.phase !== 'taking' || run.submitting) return;
  const now = Date.now();
  if (now - run.lastReport < REPORT_GAP_MS) return;
  run.lastReport = now;
  const res = await api.post(`/student/tests/${view.data.test.id}/violation`, { body: { kind, answers: view.ui.answers } });
  if (res.finished) { finish(res.result); return; }
  view.ui.violations = res.violations;
  view.ui.max = res.max_violations;
  view.ui.warn = true;
  update();
}

function startWatching() {
  if (run.detach) return;
  const onHidden = () => { if (document.hidden) report('tab_hidden'); else tick(); };
  const onBlur = () => { setTimeout(() => { if (!document.hasFocus() && !document.hidden) report('window_blur'); }, 200); };
  const onFs = () => { if (run.fullscreen && !document.fullscreenElement) report('fullscreen_exit'); };
  const block = (e) => e.preventDefault();
  const onUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('blur', onBlur);
  document.addEventListener('fullscreenchange', onFs);
  window.addEventListener('beforeunload', onUnload);
  for (const type of ['copy', 'cut', 'paste', 'contextmenu', 'dragstart']) document.addEventListener(type, block);
  run.detach = () => {
    document.removeEventListener('visibilitychange', onHidden);
    window.removeEventListener('blur', onBlur);
    document.removeEventListener('fullscreenchange', onFs);
    window.removeEventListener('beforeunload', onUnload);
    for (const type of ['copy', 'cut', 'paste', 'contextmenu', 'dragstart']) document.removeEventListener(type, block);
    run.detach = null;
    run.fullscreen = false;
  };
}

function stopWatching() {
  run.detach?.();
}

register({
  'lt.start': async () => {
    const view = currentView();
    view.ui.busy = true;
    view.ui.error = '';
    update();
    try {
      await goFullscreen();
      run.fullscreen = canFullscreen() && Boolean(document.fullscreenElement);
      applyPaper(await api.post(`/student/tests/${view.data.test.id}/start`));
    } catch (error) {
      view.ui.error = error.message;
      void leaveFullscreen();
    } finally {
      view.ui.busy = false;
      update();
    }
  },
  'lt.pick': (el) => {
    update((ui) => { ui.answers[Number(el.dataset.i)] = Number(el.dataset.j); });
  },
  'lt.resume': async () => {
    await goFullscreen();
    run.fullscreen = canFullscreen() && Boolean(document.fullscreenElement);
    update((ui) => { ui.warn = false; });
  },
  'lt.submit': async () => {
    const ui = currentView().ui;
    const blank = ui.answers.filter((a) => a == null).length;
    if (!(await confirmBox({ title: 'Submit the test?', text: blank ? `${blank} question${blank === 1 ? ' is' : 's are'} unanswered and will count as wrong. You cannot change your answers after this.` : 'You cannot change your answers after this.', label: 'Submit' }))) return;
    await send(false);
  },
});

export const routes = [list, testPage];
